import {Parameter, ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {Box, Button, Card, Checkbox, FormControlLabel, Grid} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import {observer} from 'mobx-react-lite';
import AgentAssistPanel from '../Assist/AgentAssistPanel';
import AgentInfo from './AgentInfo';
import BasicInformation from './BasicInformation';
import PolicyInformation from '../PreApprovals/Components/PolicyInformation';
import ConditionalCoverageBanking from '../PreApprovals/Components/CoverageBanking';
import CarrierPortalInformation from './CarrierPortalInformation';
import {useEffect, CSSProperties, useRef, useState, useMemo, ChangeEvent} from 'react';
import RootStore from '@/Core/mobx/RootStore';
import {IS_PRODUCTION} from '@/constants';
import {isPreviouslySubmitted, isSubmitDisabled} from './submitGate';

type ValueChanged = (p?: Parameter, value?: string | number | boolean | undefined) => void;

const getSubmitButtonStyle = (disabled: boolean): CSSProperties => ({
    marginTop: 16,
    backgroundColor: disabled ? '#B8C9C7' : '#456D69',
    color: 'white',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'FredokaRegular',
    textTransform: 'none',
});

interface PostAppWrapPanelProps {
    parameterStore: ParameterStore;
    valueChanged: ValueChanged;
}

export const PostAppWrapPanel = observer(({parameterStore, valueChanged}: PostAppWrapPanelProps) => {
    const {contactsFormStore} = RootStore.agentPortalStore;
    const {approval, prequal} = parameterStore || {};
    const [assistPanelExpanded, setAssistPanelExpanded] = useState(true);
    const [agentInfoCompleted, setAgentInfoCompleted] = useState(false);
    const [basicInformationCompleted, setBasicInformationCompleted] = useState(false);
    const [policyInformationCompleted, setPolicyInformationCompleted] = useState(false);
    const [conditionalCoverageBankingCompleted, setConditionalCoverageBankingCompleted] = useState(false);
    const [carrierPortalInformationCompleted, setCarrierPortalInformationCompleted] = useState(false);
    // First missing required field per section (null when the section is complete).
    const [agentInfoMissing, setAgentInfoMissing] = useState<string | null>(null);
    const [basicInformationMissing, setBasicInformationMissing] = useState<string | null>(null);
    const [policyInformationMissing, setPolicyInformationMissing] = useState<string | null>(null);
    const [conditionalCoverageBankingMissing, setConditionalCoverageBankingMissing] = useState<string | null>(null);
    const [carrierPortalInformationMissing, setCarrierPortalInformationMissing] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    // Lets an agent bypass the previously-submitted gate on the submit button.
    // Session-only by design: never persisted to the form.
    const [overrideSubmitted, setOverrideSubmitted] = useState(false);
    const submittingRef = useRef(false);
    const warningOffset =
        (!IS_PRODUCTION ? 76 : 0) +
        (approval?.state?.value != null && !RootStore.currentAgentStates.includes(approval.state.value) ? 80 : 0);
    const gridContainerStyle = useMemo(
        () => ({height: `calc(100vh - ${120 + warningOffset}px)`, overflowY: 'hidden' as const}),
        [warningOffset],
    );

    // Fetch recommended products if not already loaded
    useEffect(() => {
        const fetchProductsIfNeeded = async () => {
            // Check if products are not loaded
            if (
                !parameterStore.recommendedProductsResponse ||
                !parameterStore.recommendedProductsResponse.allProducts
            ) {
                try {
                    await parameterStore.fetchRecommendedProducts();
                } catch (error) {
                    console.error('Failed to fetch recommended products:', error);
                }
            }
        };

        fetchProductsIfNeeded();
    }, [parameterStore]);

    const handleSubmitToCaseManagement = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            await contactsFormStore.submitPostAppWrap({overrideSubmitted});
            // Clear the override so the freshly-recorded submitted status re-gates
            // the button instead of leaving it live for an accidental resubmit.
            setOverrideSubmitted(false);
        } catch (error) {
            console.error('Error submitting post-app-wrap', error);
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <Grid container spacing={2} style={gridContainerStyle}>
            <Grid
                item
                xs={assistPanelExpanded ? 8 : 11}
                style={{height: '100%', overflowY: 'auto', paddingTop: 40, paddingBottom: 30}}
            >
                <Card>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={12}>
                            <AgentInfo
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={1}
                                setCompleted={setAgentInfoCompleted}
                                reportMissingField={setAgentInfoMissing}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <BasicInformation
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={2}
                                setCompleted={setBasicInformationCompleted}
                                reportMissingField={setBasicInformationMissing}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <PolicyInformation
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={3}
                                setCompleted={setPolicyInformationCompleted}
                                reportMissingField={setPolicyInformationMissing}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <ConditionalCoverageBanking
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={4}
                                setCompleted={setConditionalCoverageBankingCompleted}
                                showBankingInfoProvided
                                reportMissingField={setConditionalCoverageBankingMissing}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <CarrierPortalInformation
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={5}
                                setCompleted={setCarrierPortalInformationCompleted}
                                reportMissingField={setCarrierPortalInformationMissing}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Grid>
            <Grid
                item
                xs={assistPanelExpanded ? 4 : 1}
                style={{
                    height: '100%',
                    overflowY: 'auto',
                    paddingTop: 16,
                    ...(!assistPanelExpanded && {display: 'flex', justifyContent: 'center'}),
                }}
            >
                <AgentAssistPanel
                    tab="post-app-wrap"
                    doOnCollapse={() => setAssistPanelExpanded(false)}
                    doOnExpand={() => setAssistPanelExpanded(true)}
                    onValueChangedNotes={(value) => valueChanged({...prequal?.notes, name: 'notes'}, value)}
                    nestPostAppChecklist={
                        <Box>
                            {[
                                {name: 'Agent Info', completed: agentInfoCompleted},
                                {name: 'Basic Information', completed: basicInformationCompleted},
                                {name: 'Policy Information', completed: policyInformationCompleted},
                                {
                                    name: 'Conditional Coverage Banking',
                                    completed: conditionalCoverageBankingCompleted,
                                },
                                {
                                    name: 'Carrier Portal Information',
                                    completed: carrierPortalInformationCompleted,
                                },
                            ].map((section) => (
                                <Box
                                    key={section.name}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: 8,
                                        color: section.completed ? '#456D69' : '#838383',
                                    }}
                                >
                                    <Box style={{width: 24, marginRight: 8}}>
                                        {section.completed && <CheckIcon style={{fontSize: 18, color: '#456D69'}} />}
                                    </Box>
                                    <span>{section.name}</span>
                                </Box>
                            ))}
                            {(() => {
                                const isIncomplete =
                                    !agentInfoCompleted ||
                                    !basicInformationCompleted ||
                                    !policyInformationCompleted ||
                                    !conditionalCoverageBankingCompleted ||
                                    !carrierPortalInformationCompleted;
                                // Block if the preapproval page was already submitted, or this page
                                // was already submitted for the currently-selected application status.
                                const previouslySubmitted = isPreviouslySubmitted({
                                    submitted: parameterStore.submitted,
                                    postAppWrapSubmitted: parameterStore.postAppWrapSubmitted,
                                    applicationStatus: approval?.applicationStatus?.value,
                                });
                                const isDisabled = isSubmitDisabled({
                                    isIncomplete,
                                    isSubmitting,
                                    previouslySubmitted,
                                    overrideSubmitted,
                                });
                                const missingFields = [
                                    {name: 'Agent Info', field: agentInfoMissing},
                                    {name: 'Basic Information', field: basicInformationMissing},
                                    {name: 'Policy Information', field: policyInformationMissing},
                                    {name: 'Conditional Coverage Banking', field: conditionalCoverageBankingMissing},
                                    {name: 'Carrier Portal Information', field: carrierPortalInformationMissing},
                                ].filter((s) => s.field);
                                return (
                                    <>
                                        {missingFields.length > 0 && (
                                            <Box style={{marginTop: 12}}>
                                                <Box
                                                    style={{
                                                        fontSize: 13,
                                                        fontWeight: 600,
                                                        color: '#B00020',
                                                        marginBottom: 4,
                                                    }}
                                                >
                                                    Missing required fields:
                                                </Box>
                                                {missingFields.map((s) => (
                                                    <Box key={s.name} style={{fontSize: 13, color: '#B00020'}}>
                                                        [{s.name}] {s.field}
                                                    </Box>
                                                ))}
                                            </Box>
                                        )}
                                        {parameterStore.submitted && (
                                            <Box style={{marginTop: 12, fontSize: 13, color: '#838383'}}>
                                                This form was previously submitted from the pre-approval page.
                                            </Box>
                                        )}
                                        {parameterStore.postAppWrapSubmitted && (
                                            <Box style={{marginTop: 12, fontSize: 13, color: '#838383'}}>
                                                This form was previously submitted for application status:{' '}
                                                <span style={{fontWeight: 600, color: '#456D69'}}>
                                                    {parameterStore.postAppWrapSubmitted}
                                                </span>
                                                .
                                            </Box>
                                        )}
                                        {previouslySubmitted && (
                                            <Box style={{marginTop: 8}}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={overrideSubmitted}
                                                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                                                setOverrideSubmitted(e.target.checked)
                                                            }
                                                            name="overrideSubmitted"
                                                            color="primary"
                                                        />
                                                    }
                                                    label={
                                                        <span style={{fontSize: 13, color: '#838383'}}>
                                                            Override previously-submitted check and allow resubmission
                                                        </span>
                                                    }
                                                />
                                            </Box>
                                        )}
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            disabled={isDisabled}
                                            style={getSubmitButtonStyle(isDisabled)}
                                            onClick={handleSubmitToCaseManagement}
                                        >
                                            SUBMIT APPLICATION
                                        </Button>
                                    </>
                                );
                            })()}
                        </Box>
                    }
                />
            </Grid>
        </Grid>
    );
});
